import {
  AUDIO,
  COVER_PHOTO,
  IMAGE,
  IMAGES_VIDEOS,
  TEXT,
  TEXT_OVERLAY,
  TRIBUTE_VIDEO,
  VIDEO
} from '@/constants';
import { AssetType } from '@/enums';
import { ORGANIZATION_PERMISSION_TYPE } from '@/enums/OrganizationPermissionEnum';
import { groupBy } from '@/helpers';
import i18n from '@/i18n';
import musicThumbnail from '@/resources/img/music-thumbnail.png';
import textThumbnail from '@/resources/img/text-thumbnail.svg';
import tributeVideoThumbnail from '@/resources/img/tributeVideoThumbnail.png';
import { Asset, AssetSlot, UploadRequestObject } from '@/types';

export const getAssetFilterOptions = (organizationPermissions: string[]) => {
  const keys: string[] = Object.keys(AssetType).filter((key) => isNaN(Number(key)));
  return keys
    .filter((key) => {
      const assetType = AssetType[key as keyof typeof AssetType];
      if (
        assetType === AssetType.COVER_PHOTO &&
        !organizationPermissions.includes(ORGANIZATION_PERMISSION_TYPE.COVER_PHOTO_CREATION)
      ) {
        return false;
      }
      if (
        assetType === AssetType.TRIBUTE_VIDEO &&
        !organizationPermissions.includes(ORGANIZATION_PERMISSION_TYPE.TRIBUTE_VIDEO_CREATION)
      ) {
        return false;
      }
      if (
        assetType === AssetType.TEXT &&
        !organizationPermissions.includes(ORGANIZATION_PERMISSION_TYPE.TEXT_ASSETS_CREATION)
      ) {
        return false;
      }
      if (assetType === AssetType.STOCK_CLIPS) {
        return false;
      }

      return true;
    })
    .map((key: string) => {
      return {
        value: AssetType[key as keyof typeof AssetType],
        label: i18n.t(key.toString().toLowerCase(), { ns: 'common' })
      };
    });
};

export const getPhotoAssetFilterOptions = () => {
  const allowedAssets = [AssetType.IMAGE, AssetType.VIDEO, AssetType.TEXT_OVERLAY];
  return allowedAssets.map((key) => ({
    value: key,
    label: i18n.t(AssetType[key].toLowerCase(), { ns: 'common' })
  }));
};

export const getUploadRequestObject = (key: string) => {
  let uploadRequestObject: UploadRequestObject = {
    name: '',
    route: ''
  };

  switch (key) {
    case COVER_PHOTO:
      uploadRequestObject = {
        name: 'coverPhoto',
        route: 'cover-photos'
      };

      break;
    case IMAGES_VIDEOS:
      uploadRequestObject = {
        name: 'photosAndVideos',
        route: 'photos-videos'
      };

      break;
    case TRIBUTE_VIDEO:
      uploadRequestObject = {
        name: 'tributeVideos',
        route: 'tribute-videos'
      };

      break;
    case AUDIO:
      uploadRequestObject = {
        name: 'music',
        route: 'music'
      };

      break;
    case TEXT:
      uploadRequestObject = {
        name: 'text',
        route: 'text'
      };

      break;
  }

  return uploadRequestObject;
};

export const getNumberOfAssetSlots = (
  data: AssetSlot[] | undefined,
  validTypes: string[]
): number => {
  const result = [];
  for (const [key, value] of groupBy(
    data as AssetSlot[],
    ({ slotType }: AssetSlot) => slotType?.assetType
  )) {
    if (validTypes.includes(key as string)) {
      result.push(value);
    }
  }
  return result.flat().length;
};

export function splitFileNameAndExtension(fullName: string) {
  const lastDotIndex = fullName.lastIndexOf('.');
  return {
    fileName: lastDotIndex > 0 ? fullName.substring(0, lastDotIndex) : fullName,
    extension: lastDotIndex > 0 ? fullName.substring(lastDotIndex) : ''
  };
}

export const generateFileName = (prefix: string, fileExtension: string) => {
  const timestamp = new Date().getTime();
  const normalizedName = prefix.trim().replace(/\s+/g, '-');
  return `${normalizedName}-${timestamp}.${fileExtension}`;
};

export const getThumbnailByType = (asset: Asset) => {
  switch (asset.assetType) {
    case AUDIO:
      return musicThumbnail;
    case TEXT:
      return textThumbnail;
    case AssetType.TRIBUTE_VIDEO.toString():
      if (!asset.thumbnailUrl) {
        return tributeVideoThumbnail;
      }
      return asset.thumbnailUrl;
    default:
      return asset.thumbnailUrl;
  }
};

export const getAssetTypeForOrganizationAsset = (asset: Asset) => {
  if (
    asset.assetType === TRIBUTE_VIDEO ||
    asset.assetType === VIDEO ||
    (asset.assetType === COVER_PHOTO && asset.assetName.endsWith('.mp4'))
  ) {
    return AssetType.VIDEO;
  } else if (
    asset.assetType === IMAGE ||
    asset.assetType === TEXT_OVERLAY ||
    asset.assetType === COVER_PHOTO
  ) {
    return AssetType.IMAGE;
  } else if (asset.assetType === AUDIO) {
    return AssetType.AUDIO;
  }
};

export const getAssetTypeFromUrl = (url: string): 'image' | 'video' => {
  const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov'];
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'];

  const pathname = url.split('?')[0].toLowerCase();

  if (videoExtensions.some((ext) => pathname.endsWith(ext))) {
    return 'video';
  }

  if (imageExtensions.some((ext) => pathname.endsWith(ext))) {
    return 'image';
  }

  return 'image';
};

export const isVideoAsset = (asset?: Asset | null): boolean =>
  !!asset &&
  (asset.assetType === VIDEO ||
    (!!asset.assetUrl && getAssetTypeFromUrl(asset.assetUrl) === 'video'));
